import { CaretRight } from "@phosphor-icons/react";

import type { Device, Room } from "@/lib/api";
import { cn } from "@/lib/utils";

import { RetiredChip } from "./retired-chip";
import { DeviceIcon } from "./room-devices";

const COUNT_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];

/** "two devices", never "2 devices": digits are addresses in this product. */
function countLabel(n: number, noun: string): string {
  const word = COUNT_WORDS[n] ?? String(n);
  return `${word} ${noun}${n === 1 ? "" : "s"}`;
}

function ReadOnlyDevice({ device }: { device: Device }) {
  const id = device.external_id || device.ha_device_id;
  return (
    <div className="border-border-subtle flex items-center gap-2.5 border-t px-5 py-2 text-[13.5px] first:border-t-0">
      <DeviceIcon deviceType={device.device_type} />
      <div className="min-w-0 grow">
        <div className="text-primary font-semibold">{device.name}</div>
        <div className="text-muted flex flex-wrap items-center gap-1 font-mono text-[11.5px]">
          <span>{device.brand}</span>
          <span>·</span>
          <span>{device.device_type}</span>
          {id && (
            <>
              <span>·</span>
              <span>{id}</span>
            </>
          )}
        </div>
      </div>
      {device.decommissioned && <RetiredChip />}
    </div>
  );
}

/**
 * A room that was merged into another, shown collapsed and read-only beneath
 * its survivor.
 *
 * The record keeps the absorbed room so the merge is auditable and reversible,
 * and so the retired sensor's serial stays discoverable. Nothing here can be
 * edited or deleted: it is a record of what happened, not a room.
 */
export function AbsorbedRoomRow({ room, survivorName }: { room: Room; survivorName: string }) {
  const devices = room.devices ?? [];
  const retired = devices.filter((d) => d.decommissioned).length;

  return (
    <details
      className={cn(
        "group border-border-subtle bg-surface-subtle/40 overflow-hidden rounded-md border border-dashed",
      )}
    >
      <summary className="hover:bg-surface-subtle flex cursor-pointer list-none items-center gap-3 px-4 py-2.5 [&::-webkit-details-marker]:hidden">
        <CaretRight
          className="text-muted size-4 shrink-0 transition-transform group-open:rotate-90"
          weight="light"
        />
        <div className="min-w-0 grow">
          <span className="text-muted text-[14px] font-semibold">
            {room.name || "Unnamed room"}
          </span>
          <span className="text-muted text-[13px]"> merged into {survivorName}</span>
        </div>
        <span className="text-muted shrink-0 text-[12px]">
          {countLabel(devices.length, "device")}
          {retired > 0 && `, ${countLabel(retired, "retired")}`}
        </span>
      </summary>

      <div className="border-border-subtle border-t">
        <p className="text-muted px-5 pt-3 pb-1 text-[12.5px]">
          Kept for the record, not exported. Its floor is heated by {survivorName}&apos;s
          thermostat, so the two are one room in the handoff.
        </p>
        {devices.length === 0 ? (
          <p className="text-muted px-5 py-3 text-[13.5px]">No devices.</p>
        ) : (
          devices.map((device, i) => (
            // A stable identity where one exists. Devices have no guaranteed id, so
            // fall back to name plus position rather than position alone.
            <ReadOnlyDevice
              key={device.external_id ?? device.ha_device_id ?? `${device.name}-${i}`}
              device={device}
            />
          ))
        )}
        <div className="h-2" />
      </div>
    </details>
  );
}
